import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatCLP, formatDateTime, apiError } from "../../lib/format";
import { toast } from "sonner";
import { Hourglass, Copy, X, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function PendingTransfers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/transfers/pending");
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, []);

  const copy = (token) => {
    navigator.clipboard.writeText(token).then(() => toast.success("Código copiado"));
  };

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta transferencia?")) return;
    try {
      await api.post(`/transfers/${id}/cancel`);
      toast.success("Transferencia cancelada");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const regenerate = async (id) => {
    if (!window.confirm("¿Generar un nuevo código de 4 dígitos? El código anterior dejará de funcionar.")) return;
    try {
      await api.post(`/transfers/${id}/regenerate-token`);
      toast.success("Nuevo código generado");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">Autorizaciones</div>
          <h1 className="font-display text-4xl font-black tracking-tighter flex items-center gap-3">
            Transferencias pendientes
            {items.length > 0 && (
              <span className="text-base bg-burnt text-bg font-mono px-3 py-1" data-testid="pending-count">
                {items.length}
              </span>
            )}
          </h1>
          <p className="text-sm text-inkMuted mt-2">
            Dale el código de 4 dígitos al cliente para que pueda confirmar su transferencia.
          </p>
        </div>
        <button onClick={load} className="h-10 px-3 border border-hairline text-xs font-mono uppercase tracking-widest hover:bg-bg flex items-center gap-2">
          <ArrowsClockwise size={14} /> Actualizar
        </button>
      </header>

      {loading && <div className="bento p-8 text-sm text-inkMuted">Cargando…</div>}

      {!loading && items.length === 0 && (
        <div className="bento p-12 text-center" data-testid="pending-empty">
          <Hourglass size={36} weight="duotone" className="mx-auto text-inkMuted opacity-50" />
          <div className="font-display text-xl font-bold mt-3">Sin transferencias pendientes</div>
          <div className="text-sm text-inkMuted mt-1">Cuando un cliente solicite una transferencia, aparecerá acá.</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((t) => {
          const blocked = t.status === "rechazado_token";
          return (
          <div key={t.id} data-testid={`pending-${t.id}`} className={`bento p-5 border-l-4 ${blocked ? "border-l-danger" : "border-l-burnt"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="label-mono">Cliente</div>
                <div className="font-semibold truncate">{t.sender_name}</div>
                <div className="text-xs font-mono text-inkMuted">{t.sender_rut}</div>
              </div>
              <div className="text-right">
                <div className="label-mono">Monto</div>
                <div className="font-mono text-xl font-medium text-burnt">{formatCLP(t.amount)}</div>
              </div>
            </div>

            {blocked && (
              <div className="mt-3 p-3 bg-danger/10 border border-danger/30 text-danger text-xs font-mono flex items-start gap-2" data-testid={`blocked-${t.id}`}>
                <span className="font-bold uppercase tracking-widest">Bloqueada</span>
                <span>El cliente alcanzó el máximo de intentos. Regenera el código para reabrir la autorización.</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4 text-xs font-mono border-t border-hairline pt-3">
              <Row k="Destinatario" v={t.recipient_name} />
              <Row k="RUT destino" v={t.recipient_rut} />
              <Row k="Banco" v={t.bank} />
              <Row k="Tipo cuenta" v={t.account_type} />
              <Row k="N° cuenta" v={t.account_number} />
              <Row k="Email" v={t.recipient_email} />
              {t.comment && <Row k="Comentario" v={t.comment} full />}
              <Row k="Solicitada" v={formatDateTime(t.created_at)} full />
            </div>

            <div className={`mt-4 p-4 ${blocked ? "bg-ink/80" : "bg-forest"} text-bg`}>
              <div className="text-[10px] uppercase tracking-widest text-bg/60 mb-2">Código para el cliente</div>
              <div className="flex items-center justify-between gap-3">
                <div className={`font-mono text-5xl tracking-[0.4em] font-medium ${blocked ? "line-through opacity-50" : ""}`} data-testid={`token-${t.id}`}>{t.token}</div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => copy(t.token)}
                    disabled={blocked}
                    className="h-9 px-3 border border-bg/30 text-xs font-mono uppercase tracking-widest hover:bg-bg hover:text-forest disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                    data-testid={`copy-token-${t.id}`}
                  >
                    <Copy size={13} weight="bold" /> Copiar
                  </button>
                  <button
                    onClick={() => regenerate(t.id)}
                    className="h-9 px-3 border border-bg/30 bg-bg text-forest text-xs font-mono uppercase tracking-widest hover:bg-burnt hover:text-bg hover:border-burnt transition-colors flex items-center gap-1.5"
                    data-testid={`regen-token-${t.id}`}
                    title="Generar nuevo código y resetear intentos"
                  >
                    <ArrowsClockwise size={13} weight="bold" /> Regenerar
                  </button>
                </div>
              </div>
              {t.token_attempts > 0 && (
                <div className="text-xs font-mono text-bg/70 mt-2">
                  Intentos fallidos del cliente: {t.token_attempts}/5
                </div>
              )}
              {t.token_regenerated_at && (
                <div className="text-[10px] font-mono text-bg/50 mt-1" data-testid={`regen-stamp-${t.id}`}>
                  Código regenerado el {formatDateTime(t.token_regenerated_at)}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-4 text-xs">
              <span className="text-inkMuted">
                {blocked
                  ? "Regenera el código y compártelo nuevamente con el cliente."
                  : "Espera a que el cliente confirme · estado pasará a “completado” automáticamente."}
              </span>
              <button
                onClick={() => cancel(t.id)}
                className="h-9 px-3 border border-hairline text-xs font-mono uppercase tracking-widest hover:bg-danger hover:text-bg hover:border-danger transition-colors flex items-center gap-1.5"
                data-testid={`cancel-${t.id}`}
              >
                <X size={12} weight="bold" /> Cancelar
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ k, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-inkMuted">{k}</div>
      <div className="break-words">{v || "—"}</div>
    </div>
  );
}
